// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { useMemo } from "react";
import { useGetList, useGetOne } from "react-admin";
import { ELogTimeZonePolicy, effectiveTimeZone, primaryTimeZone, } from "@sequentech/ui-core";
import { useTenantStore } from "@/providers/TenantContextProvider";
/** The timezone context of an event presentation and its elections. */
export var timeZoneContextOf = function (event, elections) {
    var _a, _b, _c, _d;
    if (elections === void 0) { elections = []; }
    var primary = primaryTimeZone(event);
    var configured = ((_b = (_a = event === null || event === void 0 ? void 0 : event.timezones) === null || _a === void 0 ? void 0 : _a.configured) === null || _b === void 0 ? void 0 : _b.length) ? event.timezones.configured : [primary];
    var zones = new Map(elections.map(function (election) { return [
        election.id,
        effectiveTimeZone(event, election.presentation),
    ]; }));
    return {
        configured: configured,
        primary: primary,
        logs: (_d = (_c = event === null || event === void 0 ? void 0 : event.timezones) === null || _c === void 0 ? void 0 : _c.logs) !== null && _d !== void 0 ? _d : ELogTimeZonePolicy.ELECTION,
        zoneOf: function (electionId) { return (electionId && zones.get(electionId)) || primary; },
    };
};
/** The timezone context of an election event, read with react-admin. */
export var useTimeZoneContext = function (electionEventId) {
    var _a;
    var tenantId = useTenantStore()[0];
    var event = useGetOne("sequent_backend_election_event", { id: electionEventId }, { enabled: !!electionEventId }).data;
    var elections = useGetList("sequent_backend_election", {
        pagination: { page: 1, perPage: 9999 },
        filter: { tenant_id: tenantId, election_event_id: electionEventId },
    }, { enabled: !!electionEventId }).data;
    // Kept by value: screens re-run effects when the context changes.
    var timezones = (_a = event === null || event === void 0 ? void 0 : event.presentation) === null || _a === void 0 ? void 0 : _a.timezones;
    var key = JSON.stringify([
        timezones !== null && timezones !== void 0 ? timezones : null,
        (elections !== null && elections !== void 0 ? elections : []).map(function (_a) {
            var _b;
            var id = _a.id, presentation = _a.presentation;
            return [
                id,
                (_b = presentation === null || presentation === void 0 ? void 0 : presentation.timezone) !== null && _b !== void 0 ? _b : null,
            ];
        }),
    ]);
    return useMemo(function () {
        var _a = JSON.parse(key), zones = _a[0], electionZones = _a[1];
        return timeZoneContextOf({ timezones: zones !== null && zones !== void 0 ? zones : undefined }, electionZones.map(function (_a) {
            var id = _a[0], timezone = _a[1];
            return ({ id: id, presentation: { timezone: timezone } });
        }));
    }, [key]);
};
